import { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Routes as AppRoutes } from '../../../routes';
import { Box, Alert, CircularProgress, Snackbar } from '@mui/material';
import { PageBreadcrumb } from '../../../shared/components/layout/PageBreadcrumb';
import {
  usePotDetailsPanelState,
  type PotDetailsPanelType,
} from '../hooks/usePotDetailsPanelState';
import { getErrorMessage } from '@billda/shared-ui';
import { useGetPotDetails } from '../hooks/useGetPotDetails';
import { useMovePotToActivePeriod } from '../hooks/useMovePotToActivePeriod';
import { useMoveToPrimaryHandler } from '../hooks/useMoveToPrimary';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { PageHeader } from '../../../shared/components/layout/PageHeader';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { ChipTabs, type ChipTab } from '../../../shared/components/ui/ChipTabs';
import { SidePanel, ResizableHandle } from '../../../shared/components/panel';
import { HelpPanel } from '../../../shared/components/help/HelpPanel';
import { POT_APPROVALS_HELP } from '../../../shared/constants/helpContent';
import { PotPageActions } from '../components/v2/PotPageActions';
import { PotOverviewContent } from '../components/v2/PotOverviewContent';
import { PotBreakdownLedger } from '../ledger/PotBreakdownLedger';
import type { PotLedgerRowActions } from '../ledger/PotLedgerRow';
import { PotApprovalPanelContent } from '../components/panel/PotApprovalPanelContent';
import { PotRejectionPanelContent } from '../components/panel/PotRejectionPanelContent';
import { RejectionManagementPanelContent } from '../components/panel/RejectionManagementPanelContent';
import { MarkPotPaidPanelContent } from '../components/panel/MarkPotPaidPanelContent';
import { AdminCancelAllocationPanel } from '../../sites/components/panel/organisms/AdminCancelAllocationPanel';
import { SiteDrawerContent } from '../components/panel/SiteDrawerContent';
import { UserDrawerContent } from '../components/panel/UserDrawerContent';
import { PlotDrawerContent } from '../components/panel/PlotDrawerContent';
import { LabourItemDrawerContent } from '../components/panel/LabourItemDrawerContent';
import { VariationDrawerContent } from '../components/panel/VariationDrawerContent';
import { AllocationDrawerContent } from '../components/panel/AllocationDrawerContent';
import { DeductionDrawerContent } from '../components/panel/DeductionDrawerContent';

const TABS: ChipTab[] = [
  { label: 'Overview', value: 'overview' },
  { label: 'Breakdown', value: 'breakdown' },
];

/**
 * Site and plot ride along in the URL so a panel deep link keeps its context.
 * Rows that belong to neither simply omit them.
 */
function panelContext(
  siteId: number | null,
  plotId: number | null
): Record<string, string> {
  const context: Record<string, string> = {};
  if (siteId != null) {
    context.siteId = siteId.toString();
  }
  if (plotId != null) {
    context.plotId = plotId.toString();
  }
  return context;
}

export default function PotDetailsPage() {
  const { potId } = useParams<{ potId: string }>();
  const navigate = useNavigate();
  const { potDetails, potResponse, isLoading, error } = useGetPotDetails(
    potId ?? null
  );
  const { isOpen, type, id, closePanel, openPanel } = usePotDetailsPanelState();

  const [activeTab, setActiveTab] = useState<'overview' | 'breakdown'>(
    'overview'
  );
  const [breakdownSearch, setBreakdownSearch] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [adminCancelAllocationId, setAdminCancelAllocationId] = useState<
    string | null
  >(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const { mutate: movePotToActivePeriodMutate } = useMovePotToActivePeriod();
  const handleMoveToPrimary = useMoveToPrimaryHandler(setSnackbar);

  const movePotToActivePeriod = useCallback(
    (params: { potId: string }) => {
      movePotToActivePeriodMutate(params, {
        onSuccess: response => {
          setSnackbar({
            open: true,
            message: `Pot moved from Week ${response.fromPeriod.weekNumber} to Week ${response.toPeriod.weekNumber}`,
            severity: 'success',
          });
          setTimeout(() => navigate(AppRoutes.finance.pots), 1500);
        },
        onError: e => {
          setSnackbar({
            open: true,
            message: getErrorMessage(e),
            severity: 'error',
          });
        },
      });
    },
    [movePotToActivePeriodMutate, navigate]
  );

  // Panel title
  const getPanelTitle = (panelType: PotDetailsPanelType | null): string => {
    switch (panelType) {
      case 'approve':
        return 'Approve Pot';
      case 'reject':
        return 'Reject Pot';
      case 'rejectionManagement':
        return 'Manage Rejection';
      case 'timeline':
        return 'Pot Timeline';
      case 'markPaid':
        return 'Mark as Paid';
      case 'adminCancel':
        return 'Cancel Allocation';
      case 'siteDetails':
        return 'Site Details';
      case 'plotDetails':
        return 'Plot Details';
      case 'labourItemDetails':
        return 'Labour Item Details';
      case 'variationDetails':
        return 'Variation Details';
      case 'allocationDetails':
        return 'Allocation Details';
      case 'deductionDetails':
        return 'Deduction Details';
      case 'userDetails':
        return 'User Details';
      default:
        return 'Details';
    }
  };

  // Action handlers
  const handleApprove = useCallback(() => {
    if (potId) openPanel('approve', potId);
  }, [openPanel, potId]);

  const handleReject = useCallback(() => {
    if (potId) openPanel('reject', potId);
  }, [openPanel, potId]);

  const handleManageRejection = useCallback(() => {
    if (potId) openPanel('rejectionManagement', potId);
  }, [openPanel, potId]);

  const handleMarkPaid = useCallback(() => {
    if (potId) openPanel('markPaid', potId);
  }, [openPanel, potId]);

  const handleMoveToActivePeriod = useCallback(() => {
    if (potId) movePotToActivePeriod({ potId });
  }, [potId, movePotToActivePeriod]);

  const onMoveToPrimary = useCallback(() => {
    if (potId) {
      handleMoveToPrimary(potId);
    }
  }, [potId, handleMoveToPrimary]);

  // Success handlers
  const handleApprovalSuccess = useCallback(() => {
    closePanel();
    navigate(AppRoutes.finance.pots);
  }, [closePanel, navigate]);

  const handleMarkPaidSuccess = useCallback(() => {
    closePanel();
    navigate(AppRoutes.finance.pots);
  }, [closePanel, navigate]);

  const handleRejectionSuccess = useCallback(() => {
    closePanel();
    navigate(AppRoutes.finance.pots);
  }, [closePanel, navigate]);

  const handleRejectionStatusChange = useCallback(() => {
    closePanel();
  }, [closePanel]);

  const handleAdminCancelSuccess = useCallback(() => {
    closePanel();
    setAdminCancelAllocationId(null);
  }, [closePanel]);

  const handleDeductionCancelled = useCallback(
    (message: string) => {
      setSnackbar({ open: true, message, severity: 'success' });
      closePanel();
    },
    [closePanel]
  );

  // Drawer handlers
  const handleOpenSiteDrawer = useCallback(
    (siteId: number) => openPanel('siteDetails', siteId.toString()),
    [openPanel]
  );

  const handleOpenPlotDrawer = useCallback(
    (plotId: number, siteId: number | null) =>
      openPanel('plotDetails', plotId.toString(), panelContext(siteId, null)),
    [openPanel]
  );

  const handleOpenLabourItemDrawer = useCallback(
    (labourItemId: number, siteId: number | null, plotId: number | null) =>
      openPanel(
        'labourItemDetails',
        labourItemId.toString(),
        panelContext(siteId, plotId)
      ),
    [openPanel]
  );

  const handleOpenVariationDrawer = useCallback(
    (variationId: string, siteId: number | null, plotId: number | null) =>
      openPanel('variationDetails', variationId, panelContext(siteId, plotId)),
    [openPanel]
  );

  const handleOpenAllocationDrawer = useCallback(
    (allocationId: string) => openPanel('allocationDetails', allocationId),
    [openPanel]
  );

  const handleOpenDeductionDrawer = useCallback(
    (deductionId: string) => openPanel('deductionDetails', deductionId),
    [openPanel]
  );

  const handleOpenUserDrawer = useCallback(
    (userId: string) => openPanel('userDetails', userId),
    [openPanel]
  );

  const handleAdminCancel = useCallback(
    (allocationId: string) => {
      setAdminCancelAllocationId(allocationId);
      openPanel('adminCancel', allocationId);
    },
    [openPanel]
  );

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const ledgerActions = useMemo<PotLedgerRowActions>(
    () => ({
      onOpenAllocation: handleOpenAllocationDrawer,
      onOpenVariation: handleOpenVariationDrawer,
      onOpenDeduction: handleOpenDeductionDrawer,
      onOpenSite: handleOpenSiteDrawer,
      onOpenPlot: handleOpenPlotDrawer,
      onOpenLabourItem: handleOpenLabourItemDrawer,
      onAdminCancel: handleAdminCancel,
    }),
    [
      handleOpenAllocationDrawer,
      handleOpenVariationDrawer,
      handleOpenDeductionDrawer,
      handleOpenSiteDrawer,
      handleOpenPlotDrawer,
      handleOpenLabourItemDrawer,
      handleAdminCancel,
    ]
  );

  // Render panel content
  const renderPanelContent = () => {
    if (
      !potDetails &&
      type !== 'siteDetails' &&
      type !== 'plotDetails' &&
      type !== 'labourItemDetails' &&
      type !== 'variationDetails' &&
      type !== 'allocationDetails' &&
      type !== 'deductionDetails' &&
      type !== 'userDetails'
    ) {
      return null;
    }

    switch (type) {
      case 'approve':
        return (
          <PotApprovalPanelContent
            potDetails={potDetails!}
            onSuccess={handleApprovalSuccess}
            onReject={handleReject}
          />
        );
      case 'reject':
        return (
          <PotRejectionPanelContent
            potDetails={potDetails!}
            onSuccess={handleRejectionSuccess}
          />
        );
      case 'rejectionManagement':
        return (
          <RejectionManagementPanelContent
            potDetails={potDetails!}
            onStatusChange={handleRejectionStatusChange}
          />
        );
      case 'timeline':
        return null;
      case 'markPaid':
        return (
          <MarkPotPaidPanelContent
            potDetails={potDetails!}
            onSuccess={handleMarkPaidSuccess}
          />
        );
      case 'adminCancel':
        if (!adminCancelAllocationId) return null;
        return (
          <AdminCancelAllocationPanel
            allocationId={adminCancelAllocationId}
            onSuccess={handleAdminCancelSuccess}
            onBack={() =>
              openPanel('allocationDetails', adminCancelAllocationId)
            }
          />
        );
      case 'siteDetails':
        if (!id) return null;
        return <SiteDrawerContent key={id} siteId={parseInt(id, 10)} />;
      case 'plotDetails':
        if (!id) return null;
        return <PlotDrawerContent key={id} plotId={parseInt(id, 10)} />;
      case 'labourItemDetails':
        if (!id) return null;
        return (
          <LabourItemDrawerContent key={id} labourItemId={parseInt(id, 10)} />
        );
      case 'variationDetails':
        if (!id) return null;
        return <VariationDrawerContent key={id} variationId={id} />;
      case 'allocationDetails':
        if (!id) return null;
        return (
          <AllocationDrawerContent
            key={id}
            allocationId={id}
            onAdminCancel={handleAdminCancel}
          />
        );
      case 'deductionDetails': {
        if (!id || !potDetails) return null;
        const deduction = potDetails.deductions.find(d => d.id === id);
        if (!deduction) return null;
        return (
          <DeductionDrawerContent
            key={id}
            deduction={deduction}
            onCancelled={handleDeductionCancelled}
          />
        );
      }
      case 'userDetails':
        if (!id) return null;
        return <UserDrawerContent key={id} userId={id} />;
      default:
        return null;
    }
  };

  // Build panel content (must be before early returns for hooks rule)
  const panelContent = useMemo(
    () => (
      <>
        {isOpen && type && (
          <>
            <ResizableHandle />
            <SidePanel
              open={isOpen}
              onClose={closePanel}
              title={getPanelTitle(type)}
            >
              {renderPanelContent()}
            </SidePanel>
          </>
        )}
        {helpOpen && (
          <>
            <ResizableHandle />
            <SidePanel
              open={helpOpen}
              onClose={() => setHelpOpen(false)}
              title="Help"
              testId="pot-details-help-drawer"
            >
              <HelpPanel helpContent={POT_APPROVALS_HELP} />
            </SidePanel>
          </>
        )}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen, type, id, helpOpen, potDetails, adminCancelAllocationId]
  );

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress data-testid="pot-details-loading-spinner" />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        Failed to load pot details. Please try again or return to the pot
        approvals page.
      </Alert>
    );
  }

  // No data state
  if (!potDetails) {
    return (
      <Alert severity="warning" sx={{ m: 3 }}>
        Pot not found.
      </Alert>
    );
  }

  const pageTitle = `${potDetails.owner.ownerName} — Wk ${potDetails.period.weekNumber}`;

  return (
    <>
      <PageContentArea isPanelOpen={isOpen || helpOpen} panel={panelContent}>
        <PageHeader
          title={
            <PageBreadcrumb
              segments={[
                { label: 'Pots', to: AppRoutes.finance.pots },
                { label: pageTitle },
              ]}
            />
          }
          helpAction={{
            onClick: () => setHelpOpen(true),
            testId: 'pot-details-help-button',
          }}
          actions={
            <PotPageActions
              potDetails={potDetails}
              onApprove={handleApprove}
              onReject={handleReject}
              onManageRejection={handleManageRejection}
              onMarkPaid={handleMarkPaid}
              onMoveToActivePeriod={handleMoveToActivePeriod}
              onMoveToPrimary={onMoveToPrimary}
            />
          }
          data-testid="pot-details-header"
        />

        <PageToolbar
          tabs={
            <ChipTabs
              tabs={TABS}
              value={activeTab}
              onChange={v => setActiveTab(v as 'overview' | 'breakdown')}
            />
          }
          searchValue={activeTab === 'breakdown' ? breakdownSearch : undefined}
          onSearchChange={
            activeTab === 'breakdown' ? setBreakdownSearch : undefined
          }
          hideFilter={activeTab !== 'breakdown'}
          data-testid="pot-details-toolbar"
        />

        {activeTab === 'overview' ? (
          <Box
            sx={{ p: { xs: 2, sm: 3 }, overflow: 'auto', flex: 1 }}
            data-testid="pot-details-page"
          >
            <PotOverviewContent
              potDetails={potDetails}
              onWorkerClick={handleOpenUserDrawer}
              onDeductionClick={handleOpenDeductionDrawer}
            />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {potResponse && (
              <PotBreakdownLedger
                pot={potResponse}
                search={breakdownSearch}
                actions={ledgerActions}
              />
            )}
          </Box>
        )}
      </PageContentArea>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
